import {
  AlertCircle,
  Award,
  BookOpen,
  Briefcase,
  Building,
  Download,
  Eye,
  FileCheck,
  FileText,
  Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { fetchTiposDocumento } from "../services/api";
import { Documento, TipoDocumento } from "../types";
import PremiumDialog from "./PremiumDialog";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface DocumentListProps {
  documentos: Documento[];
  loading: boolean;
  searchTerm?: string;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
}

type SortField = "titulo" | "seccion_nombre" | "departamento_nombre" | "fecha";
type SortDirection = "asc" | "desc";

// Mapping for document types based on tipo_documento
const getDocumentTypeInfo = (
  documento: Documento,
  tiposDocumento: TipoDocumento[],
  isDarkMode: boolean
): {
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
} => {
  const tipoDoc = documento.tipo_documento || "documento";
  const tipoInfo = tiposDocumento.find((t) => t.tipo === tipoDoc);

  // Default
  let icon = <FileText className="h-4 w-4" />;
  let label = tipoInfo?.label || "Documento";
  let description = tipoInfo?.description || "Documento oficial del BOE";
  let color = isDarkMode
    ? "bg-gray-800 text-gray-200"
    : "bg-gray-100 text-gray-800";

  // Asignar icono y color según el tipo
  switch (tipoDoc) {
    case "ley":
      icon = <BookOpen className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-blue-900 text-blue-200"
        : "bg-blue-100 text-blue-800";
      break;
    case "decreto":
      icon = <Award className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-purple-900 text-purple-200"
        : "bg-purple-100 text-purple-800";
      break;
    case "resolucion":
      icon = <FileCheck className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-green-900 text-green-200"
        : "bg-green-100 text-green-800";
      break;
    case "anuncio":
      icon = <AlertCircle className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-yellow-900 text-yellow-200"
        : "bg-yellow-100 text-yellow-800";
      break;
    case "empleo":
      icon = <Briefcase className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-orange-900 text-orange-200"
        : "bg-orange-100 text-orange-800";
      break;
    case "personal":
      icon = <Users className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-pink-900 text-pink-200"
        : "bg-pink-100 text-pink-800";
      break;
    case "ministerial":
      icon = <Building className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-indigo-900 text-indigo-200"
        : "bg-indigo-100 text-indigo-800";
      break;
    default:
      icon = <FileText className="h-4 w-4" />;
      color = isDarkMode
        ? "bg-gray-800 text-gray-200"
        : "bg-gray-100 text-gray-800";
  }

  return { icon, label, description, color };
};

const DocumentList: React.FC<DocumentListProps> = ({
  documentos,
  loading,
  searchTerm,
}) => {
  const { darkMode } = useTheme();

  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  useEffect(() => {
    const loadTiposDocumento = async () => {
      try {
        const tipos = await fetchTiposDocumento();
        setTiposDocumento(tipos);
      } catch (error) {
        console.error("Error cargando tipos de documento:", error);
      }
    };

    loadTiposDocumento();
  }, []);

  // Ordenar documentos
  const sortedDocumentos = [...documentos].sort((a, b) => {
    if (!sortField) return 0;

    const fieldA = a[sortField] || "";
    const fieldB = b[sortField] || "";

    if (sortDirection === "asc") {
      return fieldA.localeCompare(fieldB);
    } else {
      return fieldB.localeCompare(fieldA);
    }
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Sin fecha";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleDownload = async (
    documento: Documento,
    conHipervinculos?: boolean
  ) => {
    if (!documento.url_pdf) return;

    if (!conHipervinculos) {
      // Modificar la URL para usar SuperBOE
      const superBoeUrl = documento.url_pdf.replace(
        "www.boe.es",
        "www.superboe.es"
      );
      // Abrir la URL modificada en una nueva pestaña
      window.open(superBoeUrl, "_blank");
      return;
    }
  };

  // Función para resaltar coincidencias en el título
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm || searchTerm === "undefined" || !text) return text; // Verificar si searchTerm es 'undefined'

    console.log("Resaltando texto:", text, "con término:", searchTerm); // Añadir log para depuración

    // Escapar caracteres especiales en el término de búsqueda
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedSearchTerm})`, "gi"); // Crear expresión regular para resaltar

    // Dividir el texto por las coincidencias y mapear cada parte
    const parts = text.split(regex);

    return parts.map((part, index) =>
      part.match(regex) ? (
        <span
          key={index}
          className={`${
            darkMode
              ? "bg-yellow-700 font-semibold"
              : "bg-yellow-200 font-semibold"
          }`}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (documentos.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary/20 rounded-lg border border-border/50">
        <div className="flex flex-col items-center justify-center space-y-3">
          <FileText className="h-12 w-12 text-muted-foreground" />
          <h3 className="text-lg font-medium">No se encontraron documentos</h3>
          <p className="text-muted-foreground">
            Prueba a cambiar los filtros de búsqueda
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="rounded-lg overflow-hidden border border-border/50 bg-card shadow-sm">
        <Table>
          <TableHeader className="bg-secondary/30">
            <TableRow>
              <TableHead className="w-[5%]">Tipo</TableHead>
              <TableHead className="w-[45%]">Título</TableHead>
              <TableHead className="w-[20%]">Sección</TableHead>
              <TableHead className="w-[20%]">Departamento</TableHead>
              <TableHead className="w-[10%] text-center">Fecha</TableHead>
              <TableHead className="w-[10%] text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedDocumentos.map((documento) => {
              const { icon, label, description, color } = getDocumentTypeInfo(
                documento,
                tiposDocumento,
                darkMode
              );

              return (
                <TableRow key={documento.id} className="hover:bg-secondary/20">
                  <TableCell className="font-medium">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`flex justify-center items-center ${color} p-1 rounded-md cursor-help`}
                          >
                            {icon}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="flex flex-col">
                            <span className="font-bold">{label}</span>
                            <span className="text-xs opacity-90">
                              {description}
                            </span>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="break-words whitespace-normal">
                      {highlightText(
                        documento.titulo || "Sin título",
                        searchTerm || ""
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      ID: {documento.identificador}
                    </div>
                  </TableCell>
                  <TableCell>{documento.seccion_nombre}</TableCell>
                  <TableCell>{documento.departamento_nombre}</TableCell>
                  <TableCell className="text-center">
                    {formatDate(documento.fecha)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-center items-center space-x-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() =>
                                window.open(documento.url_pdf, "_blank")
                              }
                            >
                              <Eye className="h-4 w-4 text-primary" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Ver en BOE</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDownload(documento)}
                            >
                              <Download className="h-4 w-4 text-primary" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Descargar (vía SuperBOE)
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <PremiumDialog
        isOpen={showPremiumDialog}
        onOpenChange={setShowPremiumDialog}
        onSuccess={() => {}}
      />
    </div>
  );
};

export default DocumentList;
